import React, { useState, useEffect } from "react";
import { Loader } from "semantic-ui-react";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { EnvironmentalRiskMatrixform } from "../../../../api";
import { getLevelColor, LEVEL_BANDS } from "../riskMatrixLevel";
import "./RiskMatrixHeatMap.scss";

const environmentalRiskMatrixCtrl = new EnvironmentalRiskMatrixform();
const defaultFetchBySite = (accessToken, site) =>
  environmentalRiskMatrixCtrl.getEnvironmentalRiskMatricesBySite(accessToken, site);

// Genérico: sirve para cualquier metodología cuyos ítems tengan
// probability/severity 1-5 (ISO 14001, IFRS S2, ...) — quién trae los datos
// se inyecta por `fetchBySite`, así cada metodología usa su propia API/colección.
export function RiskMatrixHeatMap({ siteSelected, reload, fetchBySite = defaultFetchBySite }) {
  const { accessToken } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [items, setItems] = useState(null);

  useEffect(() => {
    (async () => {
      if (!siteSelected) {
        setItems([]);
        return;
      }
      try {
        const response = await fetchBySite(accessToken, siteSelected);
        setItems(response.code === 200 ? response.riskMatrices || [] : []);
      } catch (error) {
        console.error(error);
        setItems([]);
      }
    })();
  }, [siteSelected, reload, accessToken, fetchBySite]);

  if (!items) return <Loader active inline="centered" />;

  // counts[severity-1][probability-1] = cantidad de ítems en esa celda
  const counts = Array.from({ length: 5 }, () => Array(5).fill(0));
  items.forEach((item) => {
    const p = Number(item.probability);
    const s = Number(item.severity);
    if (p >= 1 && p <= 5 && s >= 1 && s <= 5) counts[s - 1][p - 1] += 1;
  });

  return (
    <div className="risk-matrix-heatmap">
      <p className="risk-matrix-heatmap__sub">{t("risk_matrix_heatmap_sub")}</p>
      <table className="risk-matrix-heatmap__table">
        <tbody>
          {[5, 4, 3, 2, 1].map((severity) => (
            <tr key={severity}>
              <th className="risk-matrix-heatmap__axis">{severity}</th>
              {[1, 2, 3, 4, 5].map((probability) => {
                const level = probability * severity;
                const count = counts[severity - 1][probability - 1];
                return (
                  <td
                    key={probability}
                    className="risk-matrix-heatmap__cell"
                    style={{ background: getLevelColor(level) }}
                  >
                    {count > 0 ? count : ""}
                  </td>
                );
              })}
            </tr>
          ))}
          <tr>
            <th className="risk-matrix-heatmap__axis" />
            {[1, 2, 3, 4, 5].map((probability) => (
              <th key={probability} className="risk-matrix-heatmap__axis">
                {probability}
              </th>
            ))}
          </tr>
        </tbody>
      </table>
      <div className="risk-matrix-heatmap__labels">
        <span>{t("risk_matrix_severity")} ↑</span>
        <span>{t("risk_matrix_probability")} →</span>
      </div>
      <div className="risk-matrix-heatmap__legend">
        {LEVEL_BANDS.map((band) => (
          <span key={band.labelKey}>
            <span className="risk-matrix-heatmap__legend-swatch" style={{ background: band.color }} />
            {t(band.labelKey)}
          </span>
        ))}
      </div>
    </div>
  );
}
